import { adjustQuoteDocumentForPdf } from './quotePdfAdjustments';

export type QuotePdfImageFormat = 'PNG' | 'JPEG';

interface SaveQuoteDocumentPdfOptions {
  element: HTMLElement;
  scale: number;
  imageFormat: QuotePdfImageFormat;
  quality: number;
  fileName: string;
  preloadImages?: boolean;
  fixWindowSize?: boolean;
  foreignObjectRendering?: boolean;
}

async function waitForImages(element: HTMLElement) {
  const images = Array.from(element.getElementsByTagName('img'));

  await Promise.all(
    images.map(async image => {
      if (image.complete) return;

      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () =>
          reject(new Error(`이미지를 불러올 수 없습니다: ${image.src}`));
      });
    }),
  );
}

export async function saveQuoteDocumentPdf({
  element,
  scale,
  imageFormat,
  quality,
  fileName,
  preloadImages = false,
  fixWindowSize = false,
  foreignObjectRendering = false,
}: SaveQuoteDocumentPdfOptions) {
  const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
    import('html2canvas'),
    import('jspdf'),
  ]);

  await document.fonts?.ready;

  if (preloadImages) {
    await waitForImages(element);
  }

  const canvasOptions = {
    scale,
    useCORS: true,
    logging: imageFormat === 'PNG',
    letterRendering: false,
    imageTimeout: 0,
    removeContainer: true,
    allowTaint: true,
    foreignObjectRendering,
    onclone: (clonedDoc: Document) => {
      adjustQuoteDocumentForPdf(clonedDoc);
    },
    ...(imageFormat === 'JPEG' ? { backgroundColor: '#ffffff' } : {}),
    ...(fixWindowSize
      ? {
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
        }
      : {}),
  } as Parameters<typeof html2canvas>[1];

  const canvas = await html2canvas(element, canvasOptions);

  const imageMimeType = imageFormat === 'PNG' ? 'image/png' : 'image/jpeg';
  const imageData = canvas.toDataURL(imageMimeType, quality);
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });
  const imageProperties = pdf.getImageProperties(imageData);
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const scaleToFit = Math.min(
    pageWidth / imageProperties.width,
    pageHeight / imageProperties.height,
  );
  const renderWidth = imageProperties.width * scaleToFit;
  const renderHeight = imageProperties.height * scaleToFit;

  pdf.addImage(
    imageData,
    imageFormat,
    (pageWidth - renderWidth) / 2,
    (pageHeight - renderHeight) / 2,
    renderWidth,
    renderHeight,
    undefined,
    'FAST',
  );
  pdf.save(fileName);
}
